/**
 * The authenticated shell: side navigation, top bar, and their mobile
 * equivalents, wrapped around every workspace page.
 *
 * The Owner-only items live in their own labelled "Owner" group so that
 * their absence for everyone else is a missing *section*, not a suspicious
 * gap in a list — an annotator's navigation is complete on its own terms.
 * Whether the group renders is decided by the server-computed capability
 * mirror (`profile.mayAdministerTasks`), never by comparing role strings
 * here; and hiding it is a usability choice, not a security one — `/team`
 * without authority is refused by the server and lands on the shared
 * "not available" page.
 *
 * The wordmark is the fixed LTR brand unit in both directions; the component
 * itself enforces that, so this file just places it.
 */

import { useCallback, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useLanguage } from '../i18n/language.js';
import { Wordmark } from '../shell/Wordmark.js';
import { useWorkspaceCopy } from './components.js';
import { useSession } from './session.js';
import { workspaceSlots } from './slots.js';
import { Button } from './ui.js';

/** Small geometric glyphs for the bottom navigation. Decorative. */
function NavGlyph({ name }: { readonly name: string }): ReactNode {
  const paths: Record<string, string> = {
    dashboard: 'M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z',
    tasks: 'M4 5h16M4 12h16M4 19h10',
    contributions: 'M12 3v18M5 10l7-7 7 7',
    dataRoom: 'M4 4h9l3 3h4v13H4zM4 9h16M8 13h8M8 16.5h5',
    team: 'M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 6-6M16 11a4 4 0 1 0 0-8M22 21v-1a6 6 0 0 0-4-5.65',
    earlyAccess: 'M4 5h16v14H4zM8 10h8M8 14h5',
    analysis: 'M4 19V9M10 19V5M16 19v-7M22 19V2M2 19h22',
    pool: 'M4 5h16v14H4zM8 9h8M8 13h5M17 16l2 2 3-4',
  };
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name] ?? paths['tasks']} />
    </svg>
  );
}

export function AppShell({ children }: { readonly children: ReactNode }): ReactNode {
  const copy = useWorkspaceCopy();
  const { copy: landingCopy, language, toggle } = useLanguage();
  const { session, logout } = useSession();
  const navigate = useNavigate();

  const onLogout = useCallback(() => {
    void logout().then(() => {
      void navigate('/', { replace: true });
    });
  }, [logout, navigate]);

  if (session.status !== 'authenticated') return null;
  const { account, profile } = session;
  const roleName = copy.roles[account.role] ?? account.role;
  const analysisLabel = language === 'ar' ? 'التحليل' : 'Analysis';
  const earlyAccessLabel = language === 'ar' ? 'طلبات الوصول المبكر' : 'Early access';
  const poolReviewLabel = language === 'ar' ? 'مراجعة الفيديو' : 'Video pool review';
  const poolReviewVisible =
    window.location.hostname.startsWith('staging-') ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'localhost';

  // The Data Room appears only for roles the server admits to it. Same
  // discipline as the Owner group: the capability mirror decides, and hiding
  // is usability — the server refuses a direct visit on its own.
  const primaryLinks = [
    { to: '/dashboard', label: copy.nav.dashboard, glyph: 'dashboard' },
    { to: '/tasks', label: copy.nav.tasks, glyph: 'tasks' },
    { to: '/contributions', label: copy.nav.contributions, glyph: 'contributions' },
    ...(profile.mayUseDataRoom
      ? [{ to: '/data-room', label: copy.nav.dataRoom, glyph: 'dataRoom' }]
      : []),
  ];

  return (
    <div className="mj-app-shell">
      <nav className="mj-sidenav" aria-label={copy.nav.mainNavigation}>
        <div style={{ padding: '4px var(--space-md) 12px' }}>
          <Wordmark size="md" tone="dark" />
        </div>

        <div>
          {primaryLinks.map((link) => (
            <NavLink key={link.to} to={link.to} className="mj-sidenav-link">
              <NavGlyph name={link.glyph} />
              {link.label}
            </NavLink>
          ))}
          <NavLink to="/tasks/new" className="mj-sidenav-link">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            {copy.nav.propose}
          </NavLink>
        </div>

        {account.role === 'OWNER' ? (
          <div>
            <p
              style={{
                font: 'var(--type-micro-cap)',
                letterSpacing: 'var(--type-micro-cap-tracking)',
                color: 'var(--text-on-dark-muted)',
                padding: '0 var(--space-md)',
                marginBottom: 4,
                textTransform: 'uppercase',
              }}
            >
              {copy.nav.ownerGroup}
            </p>
            <NavLink to="/team" className="mj-sidenav-link">
              <NavGlyph name="team" />
              {copy.nav.team}
            </NavLink>
            <NavLink to="/analysis" className="mj-sidenav-link">
              <NavGlyph name="analysis" />
              {analysisLabel}
            </NavLink>
            {workspaceSlots().ownerNavigation?.({ placement: 'sidebar' })}
            <NavLink to="/early-access" className="mj-sidenav-link">
              <NavGlyph name="earlyAccess" />
              {earlyAccessLabel}
            </NavLink>
            {poolReviewVisible ? (
              <NavLink to="/video-pool/review" className="mj-sidenav-link">
                <NavGlyph name="pool" />
                {poolReviewLabel}
              </NavLink>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginTop: 'auto' }}>
          <NavLink to="/onboarding" className="mj-sidenav-link">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.35-1 .9-1 1.7M12 16.8v.2" />
            </svg>
            {copy.nav.help}
          </NavLink>
        </div>
      </nav>

      <div style={{ minWidth: 0 }}>
        <header className="mj-topbar">
          {/* On mobile the sidenav is gone, so the brand moves up here. */}
          <span className="mj-topbar-brand">
            <Wordmark size="sm" tone="light" />
          </span>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-md)',
              marginInlineStart: 'auto',
              flexWrap: 'wrap',
            }}
          >
            <span
              aria-label={copy.nav.roleBadge(roleName)}
              style={{
                font: 'var(--type-caption)',
                letterSpacing: 'var(--type-caption-tracking)',
                color: 'var(--action-primary-subtle-text)',
                background: 'var(--action-primary-subtle)',
                borderRadius: 'var(--radius-tag)',
                padding: '4px 12px',
                whiteSpace: 'nowrap',
              }}
            >
              {roleName}
            </span>

            <span
              style={{
                font: 'var(--type-body-md)',
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 180,
              }}
              title={account.displayName}
            >
              {account.displayName}
            </span>

            <Button
              size="sm"
              variant="quiet"
              onClick={toggle}
              aria-label={landingCopy.nav.switchLanguage}
            >
              <span lang={language === 'en' ? 'ar' : 'en'}>
                {landingCopy.nav.otherLanguageName}
              </span>
            </Button>

            <Button size="sm" variant="outline" onClick={onLogout}>
              {copy.nav.logout}
            </Button>
          </div>
        </header>

        <main className="mj-main">{children}</main>
      </div>

      <nav className="mj-bottom-nav" aria-label={copy.nav.mainNavigation}>
        {primaryLinks.map((link) => (
          <NavLink key={link.to} to={link.to}>
            <NavGlyph name={link.glyph} />
            {link.label}
          </NavLink>
        ))}
        {account.role === 'OWNER' ? (
          <>
            {workspaceSlots().ownerNavigation?.({ placement: 'mobile' })}
            <NavLink to="/team">
              <NavGlyph name="team" />
              {copy.nav.team}
            </NavLink>
            <NavLink to="/analysis">
              <NavGlyph name="analysis" />
              {analysisLabel}
            </NavLink>
            {poolReviewVisible ? (
              <NavLink to="/video-pool/review">
                <NavGlyph name="pool" />
                {poolReviewLabel}
              </NavLink>
            ) : null}
          </>
        ) : null}
      </nav>
    </div>
  );
}
